import { useAuth0 } from "@auth0/auth0-react";
import {
    AdjustmentsHorizontalIcon,
    ArrowDownTrayIcon,
    BellIcon,
    BookOpenIcon,
    BuildingOffice2Icon,
    ChartBarIcon,
    ChatBubbleBottomCenterTextIcon,
    CircleStackIcon,
    ClockIcon,
    ComputerDesktopIcon,
    DocumentTextIcon,
    FolderOpenIcon,
    LanguageIcon,
    LinkIcon,
    TableCellsIcon,
    TagIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import { alpha, Box, Button, Theme, Typography } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

const admin = "role:administrator";
const editor = "role:editor";

const pages = [
    {
        groupTitle: "Dashboard",
        id: "dashboard",
        pages: [
            {
                id: "admin-dashboard",
                title: "Home",
                href: "dashboard",
                icon: ChartBarIcon,
                disabled: false,
                roles: [],
            },
        ],
    },
    {
        groupTitle: "Manage comments",
        id: "comments",
        pages: [
            {
                id: "admin-page-comments",
                title: "Pages",
                disabled: false,
                href: "page-comments",
                icon: ChatBubbleBottomCenterTextIcon,
                roles: [admin],
            },
            {
                id: "admin-comments",
                title: "Elements",
                disabled: false,
                href: "element-comments",
                icon: ChatBubbleBottomCenterTextIcon,
                roles: [admin],
            },
        ],
    },
    {
        groupTitle: "Media",
        id: "media",
        pages: [
            {
                id: "admin-media-downloads",
                title: "Downloads",
                href: "media/downloads",
                disabled: false,
                icon: ArrowDownTrayIcon,
                roles: [editor, admin],
            },
        ],
    },
    {
        groupTitle: "Templates",
        id: "templates",
        pages: [
            {
                id: "admin-templates",
                title: "Saved Templates",
                disabled: false,
                href: "templates",
                icon: FolderOpenIcon,
                roles: [admin],
            },
        ],
    },
    {
        groupTitle: "content",
        id: "content",
        pages: [
            {
                id: "admin-content-pages",
                title: "Pages",
                disabled: false,
                href: "pages",
                icon: DocumentTextIcon,
                roles: [editor, admin],
            },
            {
                id: "admin-content-database",
                title: "Elements",
                disabled: false,
                href: "database",
                icon: CircleStackIcon,
                roles: [editor, admin],
            },
            {
                id: "admin-versions",
                title: "Versions",
                disabled: false,
                href: "versions",
                icon: TagIcon,
                roles: [admin],
            },
        ],
    },
    {
        groupTitle: "Users",
        id: "users",
        pages: [
            {
                id: "admin-users",
                title: "Manage",
                disabled: false,
                href: "users",
                icon: UsersIcon,
                roles: [admin],
            },
        ],
    },
    {
        groupTitle: "Settings",
        id: "settings",
        pages: [
            {
                id: "admin-settings-language",
                title: "Languages",
                disabled: false,
                href: "settings/languages",
                icon: LanguageIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-bimq-parameters",
                title: "BIMQ Parameters",
                disabled: false,
                href: "settings/bimq-parameters",
                icon: BookOpenIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-disciplines",
                title: "Disciplines",
                disabled: false,
                href: "settings/disciplines",
                icon: BuildingOffice2Icon,
                roles: [admin],
            },
            {
                id: "admin-settings-phases",
                title: "Phases",
                disabled: false,
                href: "settings/phases",
                icon: ClockIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-software",
                title: "Software",
                disabled: false,
                href: "settings/software",
                icon: ComputerDesktopIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-use-cases",
                title: "Use-cases",
                disabled: false,
                href: "settings/use-cases",
                icon: AdjustmentsHorizontalIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-footnotes",
                title: "Footnotes",
                disabled: false,
                href: "settings/footnotes",
                icon: TableCellsIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-notification",
                title: "Notification",
                disabled: false,
                href: "settings/notification",
                icon: BellIcon,
                roles: [admin],
            },
            {
                id: "admin-settings-footer-links",
                title: "Footer Links",
                disabled: false,
                href: "settings/footer-links",
                icon: LinkIcon,
                roles: [admin],
            },
        ],
    },
];

type JwtToken = {
    iss: string;
    sub: string;
    aud: string[];
    exp: number;
    iat: number;
    azp: string;
    scope: string;
    permissions: string[];
};

const SidebarNav = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { getAccessTokenSilently } = useAuth0();
    const [roles, setRoles] = useState<string[]>([]);

    useEffect(() => {
        getAccessTokenSilently().then((token) => {
            const decoded = jwtDecode<JwtToken>(token);
            setIsLoading(false);
            setRoles(decoded.permissions);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box padding={2} className="flex flex-col gap-4">
            {!isLoading &&
                pages.map((item, i) => {
                    const filteredPages = item.pages.filter((p) => {
                        if (p.roles.length === 0) return true;
                        return p.roles.some((r) => roles.includes(r));
                    });

                    if (filteredPages.length === 0) return null;

                    return (
                        <Box key={i} className="">
                            <Typography
                                variant="caption"
                                color={"text.secondary"}
                                sx={{
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    marginBottom: 1,
                                    display: "block",
                                }}
                            >
                                {item.groupTitle}
                            </Typography>
                            <Box>
                                {filteredPages.map((p, i) => {
                                    return (
                                        <Box marginBottom={0.5} key={i}>
                                            <NavLink to={p.href}>
                                                {({ isActive }) => (
                                                    <Button
                                                        fullWidth
                                                        sx={{
                                                            justifyContent: "flex-start",
                                                            color: (theme: Theme) =>
                                                                isActive
                                                                    ? theme.palette.primary.main
                                                                    : theme.palette.text.primary,
                                                            backgroundColor: (theme) =>
                                                                isActive
                                                                    ? alpha(theme.palette.primary.main, 0.1)
                                                                    : "transparent",
                                                            fontWeight: isActive ? 600 : 400,
                                                            "&:hover": {
                                                                backgroundColor: (theme) =>
                                                                    isActive
                                                                        ? alpha(theme.palette.primary.main, 0.15)
                                                                        : alpha(theme.palette.text.primary, 0.1),
                                                            },
                                                        }}
                                                        startIcon={p.icon && <p.icon className="h-5 w-5 ml-2" />}
                                                        disabled={p.disabled}
                                                    >
                                                        {p.title}
                                                    </Button>
                                                )}
                                            </NavLink>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
        </Box>
    );
};

export default SidebarNav;
